import {
  ApiProperty,
  OmitType,
  PartialType,
  Type,
  IsArray,
  IsBoolean,
  IsDateString,
  IsEnum,
  IsInt,
  IsOptional,
  IsUUID,
  Min,
  ValidateNested,
} from './decorators-nest';
import { Transform } from 'class-transformer';
import { CreatePatientDto } from './patient.interface';

export enum OnboardingGoal {
  /** Concevoir naturellement */
  CONCEIVE_NATURALLY = 'conceive-naturally',
  /** Préparer mon corps */
  PREPARE_BODY = 'prepare-body',
  /** Réguler mon cycle */
  REGULATE_CYCLE = 'regulate-cycle',
  /** Mieux me comprendre */
  UNDERSTAND_CYCLE = 'understand-cycle',
  /** Accompagner ma PMA */
  SUPPORT_ART = 'support-art',
  /** Inspiration recettes */
  RECIPE_INSPIRATION = 'recipe-inspiration',
  /** Autre raison */
  OTHER = 'other',
}

export enum OnboardingSymptom {
  /** SMOP (ex SOPK) */
  PCOS = 'pcos',
  /** Fausse couche */
  MISCARRIAGE = 'miscarriage',
  /** Insuffisance ovarienne précoce */
  PREMATURE_OVARIAN_INSUFFICIENCY = 'premature-ovarian-insufficiency',
  /** Endométriose */
  ENDOMETRIOSIS = 'endometriosis',
  /** Hypothyroïdie */
  HYPOTHYROIDISM = 'hypothyroidism',
  /** Hyperthyroïdie */
  HYPERTHYROIDISM = 'hyperthyroidism',
  /** Fibrome */
  FIBROID = 'fibroid',
  /** Problème au niveau des trompes */
  TUBAL_ISSUE = 'tubal-issue',
  /** Aucun diagnostic */
  NO_DIAGNOSIS = 'no-diagnosis',
  /** Infertilité inexpliquée */
  UNEXPLAINED_INFERTILITY = 'unexplained-infertility',
  /** Cycle irrégulier */
  IRREGULAR_CYCLE = 'irregular-cycle',
  /** Arrêt de la pilule */
  PILL_DISCONTINUATION = 'pill-discontinuation',
  /** Autre raison */
  OTHER = 'other',
}

/**
 * `goal` used to be a single enum value: mobile versions released before the
 * multi-goal screen still send a bare string. Wrap it so both shapes are
 * accepted and always stored as an array.
 *
 * NOTE: class-transformer 0.3.x passes the raw value as the first positional
 * argument (not a `{ value }` object like 0.4+), so the transformer takes the
 * value directly.
 */
const toGoalArray = (value: unknown): unknown => {
  if (value === undefined || value === null) return value;
  return Array.isArray(value) ? value : [value];
};

export class CreateOnboardingDto {
  @ApiProperty({ description: 'Patient ID', example: 'uuid' })
  @IsUUID()
  public patientId: string;

  @ApiProperty({
    enum: OnboardingGoal,
    isArray: true,
    description: 'Objectifs (écran 12). Une string seule est acceptée pour les anciennes versions mobile.',
  })
  @Transform(toGoalArray)
  @IsArray()
  @IsEnum(OnboardingGoal, { each: true })
  public goal: OnboardingGoal[];

  @ApiProperty({ type: 'string', format: 'date', required: false, example: '2026-05-01' })
  @IsDateString()
  @IsOptional()
  public lastPeriodDate?: string;

  @ApiProperty({ required: false })
  @IsBoolean()
  @IsOptional()
  public isIrregular?: boolean;

  @ApiProperty({ required: false, example: 28 })
  @IsInt()
  @Min(0)
  @IsOptional()
  public avgCycleLength?: number;

  @ApiProperty({ required: false, example: 5 })
  @IsInt()
  @Min(0)
  @IsOptional()
  public avgPeriodLength?: number;

  @ApiProperty({ enum: OnboardingSymptom, isArray: true, required: false })
  @IsArray()
  @IsEnum(OnboardingSymptom, { each: true })
  @IsOptional()
  public symptoms?: OnboardingSymptom[];
}

export class UpdateOnboardingDto extends PartialType(OmitType(CreateOnboardingDto, ['patientId'] as const)) {}

export class CreateOnboardingWithoutPatientDto extends OmitType(CreateOnboardingDto, ['patientId'] as const) {}

export class RegisterWithOnboardingDto {
  @ApiProperty({ type: CreatePatientDto, description: 'Inscription de la patiente' })
  @ValidateNested()
  @Type(() => CreatePatientDto)
  public patient: CreatePatientDto;

  @ApiProperty({ type: CreateOnboardingWithoutPatientDto, description: 'Onboarding rattaché à la patiente créée' })
  @ValidateNested()
  @Type(() => CreateOnboardingWithoutPatientDto)
  public onboarding: CreateOnboardingWithoutPatientDto;
}
